import { useState, useCallback, useEffect, useMemo } from 'react';
import { getCookie } from 'cookies-next';
import { Box, Flex } from 'theme-ui';
import {
  useSettings,
  useCart,
  useProductByHandle,
} from '@backpackjs/storefront';

import { useInView } from 'react-intersection-observer';
import {
  StatefulButton,
  BackInStockModal,
  Markdown,
  GiftWithPurchase,
  RebuyModal,
  Favorite,
} from '@snippets';
import {
  useCartStatus,
  useModal,
  useSidebar,
  useBreakpointValue,
  useGiftWithPurchase,
  useGatedProduct,
} from '@hooks';

import store, { useRecoilValue } from '@store';

import { QuantitySelector } from './QuantitySelector';
import { useAddToCart } from './useAddToCart';
import { AfterPay } from '../../Header/AfterPay';
import { themed } from './AddToCart.theme';

export const AddToCart = themed(
  ({
    theme,
    product,
    selectedVariant,
    rebuyProducts,
    setIsAddToCartVisible = (isAddToCartVisible) => {
      return isAddToCartVisible;
    },
    setSyncQuantity,
    syncQuantity,
  }) => {
    const gatedPage = useRecoilValue(store.gatedPage);

    const { enableFullProductAccess } = useGatedProduct({
      product,
      gatedPage,
    });

    const productByHandle = useProductByHandle({
      handle: selectedVariant?.product?.handle,
    });
    const settings = useSettings();
    const cart = useCart();
    const [addToCartRef, inViewAddToCart, entry] = useInView({
      rootMargin: '0px 0px 10px 0px',
      threshold: 0.01,
      initialInView: true,
    });
    const mysteryItems = (settings?.mystery?.items || '').split(',');
    const rebuyModalDisabled = getCookie('rebuy_modal_disabled');

    // logic to only show Rebuy mystery item once
    const hasMysteryItem = useMemo(() => {
      return cart?.lines?.some(
        (line) =>
          line?.variant?.product?.handle &&
          mysteryItems?.includes(line?.variant?.product?.handle)
      );
    }, [cart?.updatedAt, mysteryItems]);
    let crossSells = rebuyProducts?.crossSells?.products;
    let upSells = rebuyProducts?.upSells?.products;
    if (hasMysteryItem) {
      crossSells = crossSells?.filter(
        (_product) =>
          _product?.handle && !mysteryItems?.includes(_product?.handle)
      );
      upSells = upSells?.filter(
        (_product) =>
          _product?.handle && !mysteryItems?.includes(_product?.handle)
      );
    }

    const [{ status, isSoldOut, isPreOrder, selectedPlan }, { addToCart }] =
      useAddToCart({
        product: productByHandle.product,
        selectedVariant,
      });

    const { started, finished, success, errors } = status.addItem;
    const [, { openModal }] = useModal();
    const [, { openSidebar }] = useSidebar();
    const notifyMeText = useBreakpointValue([
      'Notify Me',
      'Notify Me When Available',
    ]);
    const gwp = useGiftWithPurchase();

    const [, { setAddingToCartStatus, setUpdatingCartStatus }] =
      useCartStatus();

    const [quantity, setQuantity] = useState(syncQuantity || 1);
    const [isMaxed, setIsMaxed] = useState(false);
    const [isOverMax, setIsOverMax] = useState(false);
    const [qtyInCart, setQtyInCart] = useState(0);

    const {
      maxQty,
      errorMessage = 'Please contact bulkorders@brumate.com for orders over 150.',
      ignoredProducts,
    } = settings?.cart?.bulkOrdering || {};
    const cartItems = cart?.lines;

    const noBackInStockOption =
      isSoldOut && !isPreOrder && product?.tags?.includes('no-bis');

    const text =
      !status?.inventoryStatus?.started && product
        ? noBackInStockOption
          ? 'Sold Out'
          : isSoldOut && !isPreOrder
          ? notifyMeText
          : isPreOrder
          ? 'Pre-order'
          : selectedPlan
          ? 'Subscribe'
          : 'Add to Bag'
        : 'Loading...';

    const ignoredFromMaxQty = useMemo(
      () =>
        ignoredProducts?.some(
          ({ product: ignoredProduct }) =>
            ignoredProduct.handle === product?.handle
        ),
      [product?.handle, ignoredProducts]
    );

    const checkIfCartIsMaxedOnQtyChange = useCallback(() => {
      if (!maxQty) return false;
      const totalProductsInCart =
        cartItems?.reduce((total, { variant, quantity: itemQty }) => {
          if (
            ignoredProducts?.some(
              ({ product: ignoredProduct }) =>
                ignoredProduct.handle === variant.product.handle
            )
          ) {
            return total;
          }
          return total + itemQty;
        }, 0) || 0;
      setQtyInCart(totalProductsInCart);
      setIsMaxed(totalProductsInCart + quantity >= maxQty);
      setIsOverMax(totalProductsInCart + quantity > maxQty);
      return true;
    }, [quantity, maxQty, cart?.updatedAt, ignoredProducts]);

    const setGlobalCartStatusOnAdding = useCallback(() => {
      setAddingToCartStatus({ started, finished, success, errors });
      setUpdatingCartStatus({ started, finished, success, errors });
      const defaultStatus = {
        started: false,
        finished: false,
        success: false,
        errors: [],
      };
      return () => {
        setAddingToCartStatus(defaultStatus);
        setUpdatingCartStatus(defaultStatus);
      };
    }, [started, finished, success]);

    const handleOutOfStock = useCallback(() => {
      openModal(
        <BackInStockModal
          legacyResourceId={selectedVariant?.legacyResourceId}
          title={product?.title}
        />
      );
    }, [selectedVariant?.id, product?.title]);

    useEffect(() => {
      setGlobalCartStatusOnAdding();
    }, [started, finished, success]);
    useEffect(() => {
      checkIfCartIsMaxedOnQtyChange();
    }, [quantity, maxQty, cart?.updatedAt, ignoredProducts]);

    const isNotifyMe = isSoldOut && !isPreOrder;
    const overMaxDisabled = isOverMax && !isNotifyMe && !ignoredFromMaxQty;

    useEffect(() => {
      const handleScroll = () => {
        if (!inViewAddToCart && entry?.target) {
          const rect = entry?.target?.getBoundingClientRect();
          if (rect?.top < window.scrollY) {
            setIsAddToCartVisible(false);
          } else {
            setIsAddToCartVisible(true);
          }
        } else {
          setIsAddToCartVisible(inViewAddToCart);
        }
      };
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [entry?.target, inViewAddToCart]);

    if (!enableFullProductAccess) return null;

    return (
      <Box
        ref={addToCartRef}
        sx={{
          mt: [10, 13],
          mb: [11, 13],
          width: '100%',
        }}
      >
        <Box data-comp={AddToCart.displayName} sx={theme.wrapper}>
          <QuantitySelector
            quantity={syncQuantity || quantity}
            setQuantity={(qty) => {
              if (typeof setSyncQuantity === 'function') {
                setSyncQuantity(qty);
              }
              setQuantity(qty);
            }}
            isMaxed={isMaxed && !ignoredFromMaxQty}
            maxQty={maxQty}
            qtyInCart={qtyInCart}
          />

          <StatefulButton
            disabled={!product || overMaxDisabled || noBackInStockOption}
            loading={
              !status?.inventoryStatus?.finished &&
              status?.inventoryStatus?.started
            }
            sx={{
              ...theme.button,
              ...(isNotifyMe || overMaxDisabled || noBackInStockOption
                ? {
                    ...theme.button.unavailable,
                    ...(selectedPlan
                      ? theme.button.unavailable.subscribe
                      : {
                          ...theme.button.unavailable.oneTime,
                          borderColor:
                            overMaxDisabled || noBackInStockOption
                              ? 'gray'
                              : 'text',
                          color:
                            overMaxDisabled || noBackInStockOption
                              ? 'mediumGray'
                              : 'mediumDarkGray',
                          ':hover': {
                            borderColor:
                              overMaxDisabled || noBackInStockOption
                                ? 'gray'
                                : 'text',
                            color:
                              overMaxDisabled || noBackInStockOption
                                ? 'mediumGray'
                                : 'text',
                            bg: 'background',
                          },
                        }),
                  }
                : {
                    ...theme.button.available,
                    ...(selectedPlan
                      ? theme.button.available.subscribe
                      : theme.button.available.oneTime),
                  }),
              cursor:
                !product || overMaxDisabled || noBackInStockOption
                  ? 'default'
                  : 'pointer',
            }}
            {...status.addItem}
            text={{
              default: text,
              started: 'Adding',
              success: 'Added',
              error: 'Failed To Add',
            }}
            onClick={() => {
              if (isNotifyMe) {
                handleOutOfStock();
              } else {
                addToCart({
                  quantity,
                  callback: (_cart) => {
                    if (gwp.isEligible(_cart)) {
                      openModal(<GiftWithPurchase />);
                    } else if (
                      (crossSells?.length || upSells?.length) &&
                      rebuyModalDisabled !== 'true'
                    ) {
                      openModal(
                        <RebuyModal
                          rebuyProducts={rebuyProducts}
                          productHandle={product.handle}
                          disabledOnClose
                        />
                      );
                    } else {
                      openSidebar('cart');
                    }
                  },
                  openCart: false,
                });
              }
            }}
          />
          <Favorite selectedVariant={selectedVariant} isPdp />
        </Box>

        <Flex
          sx={{
            ...theme.afterpay,
            mt: ['12px'],
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <AfterPay
            currency={selectedVariant?.currencyCode}
            variantId={selectedVariant?.legacyResourceId}
            price={selectedVariant?.price}
          />
        </Flex>

        {isMaxed && (
          <Markdown
            text={errorMessage}
            sx={{ mt: 7, a: { textDecoration: 'none' } }}
            textSx={{ variant: 'text.label.2', color: 'red' }}
          />
        )}
      </Box>
    );
  }
);

AddToCart.displayName = 'AddToCart';